import type { ReactNode } from 'react';
import cx from 'clsx';

import styles from './codeBlock.module.css';

/**
 * Stable, rule-free class on the container. `codeBlock.module.css` hashes its own class names,
 * so this is the hook for stylesheets that have to reach in from outside:
 *
 *   .myWrapper :global(.Snippet) pre { background: transparent; }
 *
 * Prefer `className`, or one of the props below, when you control the call site.
 */
export const CODE_BLOCK_CLASS = 'Snippet';

type CodeBlockContainerProps = {
  /** A `CodeBlock`, plus any chrome the caller layers over it (a loading overlay, say). */
  children: ReactNode;
  className?: string;
  /**
   * The block's own outline. Turn it off when the caller already wraps the block in its own
   * container, so the two borders don't stack.
   */
  withBorder?: boolean;
  /**
   * Which surface the code sits on. `'primary'` is for a block inside a container whose own
   * chrome is already `bg-ui-secondary` — TabbedSnippet's tab strip — so the two stay distinct.
   */
  surface?: 'secondary' | 'primary';
  /** Positions the copy button `CodeBlock` renders as its `action` over the block. */
  copyable?: boolean;
  /** Caps the block's height, for a caller that pairs it with a show-all control. */
  expandable?: boolean;
  /**
   * How a line too long for the block behaves. `'wrap'` breaks it onto the next line; `'nowrap'`
   * keeps it on one line and scrolls the block sideways, for code where a line break would be
   * misleading — a shell command, an import statement.
   */
  overflow?: 'wrap' | 'nowrap';
  /** Leaves Prism's own line-highlight colors alone instead of applying ours. */
  useDefaultHighlighting?: boolean;
};

/**
 * The container a `CodeBlock` renders inside. Owns the block's chrome — border, surface,
 * scrolling, and the positioning context that the copy button and loading overlay hang off —
 * so the three snippet components don't each re-derive it from raw class names.
 *
 * `CodeBlock` itself carries no classes beyond the ones Prism's plugins look for; every rule
 * that reaches the `<pre>` and `<code>` is anchored on this container.
 */
export function CodeBlockContainer({
  children,
  className,
  withBorder = true,
  surface = 'secondary',
  copyable,
  expandable,
  overflow = 'wrap',
  useDefaultHighlighting: defaultHighlighting,
}: CodeBlockContainerProps) {
  return (
    <div
      className={cx(styles.snippet, CODE_BLOCK_CLASS, className, {
        [styles.borderless]: !withBorder,
        [styles.surfacePrimary]: surface === 'primary',
        [styles.copyable]: copyable,
        [styles.expandable]: expandable,
        [styles.nowrap]: overflow === 'nowrap',
        [styles.defaultHighlighting]: defaultHighlighting,
      })}
    >
      {children}
    </div>
  );
}
